import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { adminFetch, fmtNum, fmtPct } from './api';

export function PageHeader({ title, subtitle, right }) {
  return (
    <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
      <div>
        <h1 className="font-display text-2xl font-bold text-text">{title}</h1>
        {subtitle && <p className="text-sm text-text-muted mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function KpiCard({ label, value, sub, accent = false, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      className={`rounded-xl border border-border p-4 ${accent ? 'bg-gradient-brand-soft' : 'bg-surface'}`}
    >
      <div className="text-xs uppercase tracking-wide text-text-muted">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-text">{value}</div>
      {sub && <div className="mt-1 text-xs text-text-muted">{sub}</div>}
    </motion.div>
  );
}

export function CountCard({ label, n, sub, accent, delay }) {
  return <KpiCard label={label} value={fmtNum(n)} sub={sub} accent={accent} delay={delay} />;
}

export function RateCard({ label, rate, sub, healthy, delay }) {
  let tone = '';
  if (healthy && rate != null) {
    const ok = healthy(rate);
    tone = ok === true ? 'text-emerald-400'
      : ok === false ? 'text-red-400'
      : 'text-amber-400';
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: delay || 0 }}
      className="rounded-xl border border-border p-4 bg-surface"
    >
      <div className="text-xs uppercase tracking-wide text-text-muted">{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold ${tone || 'text-text'}`}>{fmtPct(rate)}</div>
      {sub && <div className="mt-1 text-xs text-text-muted">{sub}</div>}
    </motion.div>
  );
}

export function Section({ title, action, children }) {
  return (
    <section className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold text-text">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function StatusPill({ children, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-surface-2 text-text-muted border-border',
    sent: 'bg-accent/10 text-accent border-accent/30',
    followup: 'bg-violet/10 text-violet border-violet/30',
    replied: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    bounced: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    unsubscribed: 'bg-red-500/10 text-red-400 border-red-500/30',
    click: 'bg-accent-2/10 text-accent-2 border-accent-2/30',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[11px] rounded-full border ${tones[tone] || tones.neutral} capitalize`}>
      {children}
    </span>
  );
}

export function EmptyState({ title, hint }) {
  return (
    <div className="rounded-xl border border-dashed border-border p-10 text-center text-text-muted bg-surface/40">
      <div className="text-text font-medium mb-1">{title}</div>
      {hint && <div className="text-sm">{hint}</div>}
    </div>
  );
}

export function ErrorBlock({ error, onRetry }) {
  return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">
      <div className="font-medium mb-1">Failed to load</div>
      <div className="text-red-300/80">{error.message || String(error)}</div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-3 text-xs underline hover:text-red-200"
        >
          Retry
        </button>
      )}
    </div>
  );
}

// Debounced search input used across recent-activity sections.
export function SearchInput({ value, onChange, placeholder = 'Search…', width = 'w-56' }) {
  return (
    <div className={`relative ${width}`}>
      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-surface border border-border rounded-lg pl-8 pr-7 py-1 text-sm focus:outline-none focus:border-accent/50"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text p-0.5"
          aria-label="Clear search"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}

// Prev / Next pagination with "Showing X–Y of Z". Hides controls when only one page.
export function Pagination({ page, pageSize, total, onPage, loading, unitLabel = 'rows' }) {
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);

  if (total <= pageSize) {
    return (
      <div className="mt-2 text-xs text-text-muted">
        {fmtNum(total)} {total === 1 ? unitLabel.replace(/s$/, '') : unitLabel}
      </div>
    );
  }

  return (
    <div className="mt-3 flex items-center justify-between text-xs text-text-muted">
      <div>
        Showing <span className="text-text">{fmtNum(start)}</span>–<span className="text-text">{fmtNum(end)}</span> of{' '}
        <span className="text-text">{fmtNum(total)}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPage(page - 1)}
          disabled={page === 0 || loading}
          className="px-3 py-1 bg-surface border border-border rounded-lg hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <span>Page {page + 1} of {lastPage + 1}</span>
        <button
          onClick={() => onPage(page + 1)}
          disabled={page >= lastPage || loading}
          className="px-3 py-1 bg-surface border border-border rounded-lg hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}

// Hook: paginated + debounced-search fetch against an admin endpoint that accepts
// `?limit=&offset=&q=`. `dataKey` selects the array field on the response (e.g. 'events' or 'rows').
export function usePaginatedSearch({ endpoint, pageSize = 25, refreshKey = 0, dataKey = 'rows', extraParams = null }) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const extraKey = extraParams ? JSON.stringify(extraParams) : '';

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => { setPage(0); }, [debounced, extraKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    const params = new URLSearchParams({
      limit: String(pageSize),
      offset: String(page * pageSize),
    });
    if (debounced) params.set('q', debounced);
    if (extraParams) {
      for (const [k, v] of Object.entries(extraParams)) {
        if (v != null) params.set(k, String(v));
      }
    }
    adminFetch(`${endpoint}?${params}`)
      .then((r) => { if (!cancelled) setResp(r); })
      .catch((e) => { if (!cancelled) setErr(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, pageSize, page, debounced, refreshKey, extraKey]);

  return {
    query, setQuery, debounced, page, setPage,
    rows: resp ? (resp[dataKey] || []) : null,
    total: resp ? resp.total : 0,
    loading,
    err,
    pageSize,
  };
}

// Wrapper that gives a table both x and y scroll within rounded borders.
// Use for any recent-activity / list table — sticky thead keeps headers visible.
export function ScrollTable({ children, maxHeight = 'max-h-[70vh]', className = '' }) {
  return (
    <div className={`rounded-xl border border-border bg-surface overflow-hidden ${className}`}>
      <div className={`overflow-auto ${maxHeight}`}>
        {children}
      </div>
    </div>
  );
}
